/**
 * Headline counts, coverage, and what needs attention.
 *
 * The stat-tile-plus-Paper layout follows BEETS' RequirementsHub. The numbers
 * are the ones the model can actually answer: how much is unresolved, how much
 * is covered by a verification case, and what does not hang together.
 */

import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../api'
import type { CoverageGroup, Finding, Summary } from '../schema'
import { dashboardLinks } from './dashboardLinks'
import RequirementStatus from './RequirementStatus'

interface Loaded {
  summary: Summary
  bySubsystem: CoverageGroup[]
  byLevel: CoverageGroup[]
  findings: Finding[]
}

export default function Dashboard() {
  const [data, setData] = useState<Loaded | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  useEffect(() => {
    Promise.all([
      api.summary(),
      api.coverage('subsystem'),
      api.coverage('level'),
      api.findings(),
    ])
      .then(([summary, bySubsystem, byLevel, findings]) =>
        setData({ summary, bySubsystem, byLevel, findings: findings.items }),
      )
      .catch((failure: ApiError) => setError(failure))
  }, [])

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const { summary, findings } = data
  const verifiedShare = summary.requirements
    ? Math.round((summary.verified / summary.requirements) * 100)
    : 0

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The <code>.sysml</code> files in the repository are the record. This database is a
          working copy of the branch you are on; a save writes the files back and commits them.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Tile label="Requirements" value={summary.requirements} to="/requirements" />
        <Tile
          label="Unresolved"
          value={summary.unresolved}
          hint="tbd, tbr or tbc"
          tone={summary.unresolved ? 'warning' : 'default'}
          to={dashboardLinks.unresolved}
        />
        <Tile
          label="Not covered"
          value={summary.unverified}
          hint="no verification case names them"
          tone={summary.unverified ? 'warning' : 'default'}
          to={dashboardLinks.unverified}
        />
        <Tile
          label="Orphans"
          value={summary.orphans}
          hint="no source, below L0"
          tone={summary.orphans ? 'warning' : 'default'}
          to={dashboardLinks.orphans}
        />
        <Tile label="Test cases" value={summary.cases} to="/verification" />
        <Tile label="Interfaces" value={summary.interfaces} to="/interfaces" />
      </Grid>

      {/* Its own requests and its own failure: the rest of the page does not wait for it. */}
      <RequirementStatus />

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Verification coverage
        </Typography>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress
              variant="determinate"
              value={verifiedShare}
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {summary.verified} of {summary.requirements} covered by a case ({verifiedShare}%)
          </Typography>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CoverageTable title="By subsystem" groups={data.bySubsystem} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CoverageTable title="By level" groups={data.byLevel} />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1">Findings</Typography>
          <Chip
            size="small"
            label={findings.length}
            color={findings.length ? 'warning' : 'success'}
          />
        </Stack>
        {findings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing inconsistent found.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {Object.entries(groupBy(findings, (f) => f.check)).map(([check, group]) => (
              <Typography key={check} variant="body2" color="text.secondary">
                <strong>{check}</strong> — {group.length}
              </Typography>
            ))}
            <Box sx={{ pt: 1 }}>
              <RouterLink to="/findings">See all findings</RouterLink>
            </Box>
          </Stack>
        )}
      </Paper>
    </Stack>
  )
}

function Tile({
  label,
  value,
  hint,
  tone = 'default',
  to,
}: {
  label: string
  value: number
  hint?: string
  tone?: 'default' | 'warning'
  to?: string
}) {
  const body = (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        transition: 'border-color 120ms',
        border: 1,
        borderColor: 'transparent',
        ...(to && { '&:hover': { borderColor: 'primary.main' } }),
      }}
    >
      <Typography
        variant="h4"
        sx={{ fontWeight: 600, color: tone === 'warning' ? 'warning.main' : 'text.primary' }}
      >
        {value.toLocaleString()}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          {hint}
        </Typography>
      )}
    </Paper>
  )

  return (
    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
      {to ? (
        <RouterLink to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
          {body}
        </RouterLink>
      ) : (
        body
      )}
    </Grid>
  )
}

function CoverageTable({ title, groups }: { title: string; groups: CoverageGroup[] }) {
  const rows = groups.filter((group) => group.total > 0)
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing recorded.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {rows.map((group) => (
            <Box key={group.key}>
              <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                <Typography variant="body2">{group.key}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.verified}/{group.total} covered
                  {group.unresolved > 0 && ` · ${group.unresolved} unresolved`}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={group.total ? (group.verified / group.total) * 100 : 0}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  )
}

function groupBy<T>(items: T[], key: (item: T) => string): Record<string, T[]> {
  const out: Record<string, T[]> = {}
  for (const item of items) {
    const k = key(item)
    ;(out[k] ??= []).push(item)
  }
  return out
}
